<template>
  <div class="entries-page">
    <header class="entries-header">
      <div>
        <button class="back-button" type="button" @click="router.back()">
          <i class="fa-solid fa-arrow-left" />
          목록
        </button>
        <p class="eyebrow">{{ promotion.manufacturerName || manufacturerName || "제조사" }}</p>
        <h1>{{ promotion.title || title || "프로모션 참여자" }}</h1>
        <p class="description">
          참여자 정보를 조회하고 선택/검색/전체 범위로 엑셀을 다운로드합니다.
        </p>
      </div>
      <div class="header-actions">
        <a-button @click="resetFilters">
          <i class="fa-solid fa-rotate-right" />
          초기화
        </a-button>
        <a-button
          :disabled="selectedRowKeys.length === 0"
          :loading="exporting"
          @click="downloadEntries('selected')"
        >
          <i class="fa-solid fa-check-to-slot" />
          선택 다운로드
        </a-button>
        <a-button :loading="exporting" @click="downloadEntries('filtered')">
          <i class="fa-solid fa-filter" />
          검색 다운로드
        </a-button>
        <a-button type="primary" :loading="exporting" @click="downloadEntries('all')">
          <i class="fa-solid fa-download" />
          전체 다운로드
        </a-button>
      </div>
    </header>

    <section class="filter-panel">
      <div class="search-combo">
        <a-select v-model:value="filters.searchField" class="search-field">
          <a-select-option value="name">이름</a-select-option>
          <a-select-option value="phoneNumber">번호</a-select-option>
          <a-select-option value="hospitalName">병원</a-select-option>
          <a-select-option value="hospitalAddress">주소</a-select-option>
        </a-select>
        <a-input-search
          v-model:value="filters.keyword"
          placeholder="검색어를 입력하세요"
          allow-clear
          enter-button
          @search="search"
        />
      </div>
      <a-select
        v-model:value="filters.status"
        class="status-filter"
        allow-clear
        placeholder="전체 처리 상태"
        @change="search"
      >
        <a-select-option value="completed">완료</a-select-option>
        <a-select-option value="coupon_pending">쿠폰 대기</a-select-option>
        <a-select-option value="failed">실패</a-select-option>
      </a-select>
      <div class="selected-chip" :class="{ active: selectedRowKeys.length > 0 }">
        선택 {{ selectedRowKeys.length.toLocaleString() }}명
      </div>
    </section>

    <section class="table-panel">
      <div class="table-summary">
        <strong>{{ total.toLocaleString() }}</strong>
        <span>명의 참여자</span>
      </div>
      <a-table
        :columns="columns"
        :data-source="items"
        :loading="loading"
        :row-key="(record) => record._id"
        :pagination="paginationConfig"
        :row-selection="rowSelection"
        :custom-row="customRow"
        table-layout="fixed"
        @change="handleTableChange"
      >
        <template #bodyCell="{ column, record }">
          <template v-if="column.key === 'name'">
            <div class="person-cell">
              <strong>{{ record.name || "-" }}</strong>
              <span>{{ record.kakaoUserId || record.customerId || "-" }}</span>
            </div>
          </template>
          <template v-else-if="column.key === 'phoneNumber'">
            <span class="mono">{{ record.phoneNumber || "-" }}</span>
          </template>
          <template v-else-if="column.key === 'role'">
            <a-tag>{{ record.roleLabel || roleLabel(record.role) || "-" }}</a-tag>
          </template>
          <template v-else-if="column.key === 'hospitalName'">
            {{ hospitalDisplayName(record) }}
          </template>
          <template v-else-if="column.key === 'createdAt'">
            {{ formatDate(record.createdAt) }}
          </template>
        </template>
      </a-table>
    </section>

    <a-drawer
      v-model:open="detailOpen"
      width="520"
      title="참여자 상세"
      placement="right"
    >
      <template v-if="selectedEntry">
        <a-descriptions bordered :column="1" size="small">
          <a-descriptions-item label="이름">
            {{ selectedEntry.name || "-" }}
          </a-descriptions-item>
          <a-descriptions-item label="번호">
            {{ selectedEntry.phoneNumber || "-" }}
          </a-descriptions-item>
          <a-descriptions-item label="직급">
            {{ selectedEntry.roleLabel || roleLabel(selectedEntry.role) || "-" }}
          </a-descriptions-item>
          <a-descriptions-item label="병원">
            {{ hospitalDisplayName(selectedEntry) }}
          </a-descriptions-item>
          <a-descriptions-item label="주소">
            {{ selectedEntry.hospitalAddress || "-" }}
          </a-descriptions-item>
          <a-descriptions-item label="참여일">
            {{ formatDate(selectedEntry.createdAt) }}
          </a-descriptions-item>
          <a-descriptions-item label="선택 동의">
            <div v-if="optionalConsentItems.length" class="consent-list">
              <span
                v-for="item in optionalConsentItems"
                :key="item.key || item.label"
                class="consent-pill"
                :class="{ agreed: item.agreed }"
              >
                {{ item.key || item.label }} · {{ item.agreed ? "동의" : "미동의" }}
              </span>
            </div>
            <span v-else>{{ selectedEntry.optionalConsentLabel || "미확인" }}</span>
          </a-descriptions-item>
        </a-descriptions>
      </template>
    </a-drawer>
  </div>
</template>

<script setup>
import { computed, onMounted, reactive, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import { message } from "ant-design-vue";
import dayjs from "dayjs";
import {
  PromotionManagementApi,
  saveExcelBlob,
} from "./api";

const route = useRoute();
const router = useRouter();
const promotionId = route.params.promotionId;
const title = route.query.title || "";
const manufacturerName = route.query.manufacturerName || "";

const loading = ref(false);
const exporting = ref(false);
const items = ref([]);
const total = ref(0);
const promotion = ref({});
const selectedRowKeys = ref([]);
const selectedEntry = ref(null);
const detailOpen = ref(false);
const pagination = reactive({
  page: 1,
  size: 20,
});
const filters = reactive({
  searchField: "name",
  keyword: "",
  status: undefined,
});

const columns = [
  {
    title: "이름",
    dataIndex: "name",
    key: "name",
    width: 160,
    ellipsis: true,
  },
  {
    title: "번호",
    dataIndex: "phoneNumber",
    key: "phoneNumber",
    width: 150,
    ellipsis: true,
  },
  {
    title: "직급",
    dataIndex: "role",
    key: "role",
    width: 120,
  },
  {
    title: "병원",
    dataIndex: "hospitalName",
    key: "hospitalName",
    width: 230,
    ellipsis: true,
  },
  {
    title: "주소",
    dataIndex: "hospitalAddress",
    key: "hospitalAddress",
    width: 360,
    ellipsis: true,
  },
  {
    title: "참여일",
    dataIndex: "createdAt",
    key: "createdAt",
    width: 160,
  },
];

const paginationConfig = computed(() => ({
  current: pagination.page,
  pageSize: pagination.size,
  total: total.value,
  showSizeChanger: true,
  pageSizeOptions: ["20", "50", "100"],
  showTotal: (value) => `총 ${value.toLocaleString()}건`,
}));

const rowSelection = computed(() => ({
  selectedRowKeys: selectedRowKeys.value,
  preserveSelectedRowKeys: true,
  onChange: (keys) => {
    selectedRowKeys.value = keys;
  },
}));

const optionalConsentItems = computed(() => {
  const snapshot = selectedEntry.value?.kakaoConsentSnapshot;
  if (!snapshot || snapshot.status !== "synced") return [];
  return Array.isArray(snapshot.items) ? snapshot.items : [];
});

const requestParams = () => ({
  page: pagination.page,
  size: pagination.size,
  searchField: filters.searchField,
  keyword: filters.keyword,
  status: filters.status,
});

const fetchPromotion = async () => {
  try {
    const response = await PromotionManagementApi.getPromotion(promotionId);
    promotion.value = response.data?.data || {};
  } catch (error) {
    promotion.value = {};
  }
};

const fetchEntries = async () => {
  loading.value = true;
  try {
    const response = await PromotionManagementApi.getEntries(
      promotionId,
      requestParams(),
    );
    const data = response.data?.data || {};
    items.value = data.items || [];
    total.value = data.total || 0;
    if (data.promotion) promotion.value = data.promotion;
    pagination.page = data.page || pagination.page;
    pagination.size = data.size || pagination.size;
  } catch (error) {
    message.error("참여자 목록을 불러오지 못했습니다.");
  } finally {
    loading.value = false;
  }
};

const search = () => {
  pagination.page = 1;
  selectedRowKeys.value = [];
  fetchEntries();
};

const resetFilters = () => {
  filters.searchField = "name";
  filters.keyword = "";
  filters.status = undefined;
  search();
};

const handleTableChange = (pageInfo) => {
  pagination.page = pageInfo.current || 1;
  pagination.size = pageInfo.pageSize || 20;
  fetchEntries();
};

const customRow = (record) => ({
  onClick: () => {
    selectedEntry.value = record;
    detailOpen.value = true;
  },
});

const downloadEntries = async (scope) => {
  if (scope === "selected" && selectedRowKeys.value.length === 0) {
    message.warning("다운로드할 참여자를 선택해주세요.");
    return;
  }
  exporting.value = true;
  try {
    const payload =
      scope === "selected"
        ? { scope, entryIds: selectedRowKeys.value }
        : {
            scope,
            keyword: scope === "filtered" ? filters.keyword : "",
            status: scope === "filtered" ? filters.status : "",
            searchField: scope === "filtered" ? filters.searchField : "all",
          };
    const response = await PromotionManagementApi.exportEntries(
      promotionId,
      payload,
    );
    const fileLabel = promotion.value?.title || title || "프로모션_참여자";
    saveExcelBlob(
      response,
      `${fileLabel}_${dayjs().format("YYYYMMDD_HHmmss")}.xlsx`,
    );
    message.success("참여자 다운로드를 시작했습니다.");
  } catch (error) {
    message.error(error.response?.data?.message || "다운로드에 실패했습니다.");
  } finally {
    exporting.value = false;
  }
};

const roleLabel = (role) => {
  const labels = {
    HOSPITAL_HEAD: "대표원장",
    EMPLOYED_DOCTOR: "봉직의",
    PUBLIC_HEALTH_DOCTOR: "공보의",
    HOSPITAL_DIRECTOR: "병원실장",
    HOSPITAL_STAFF: "병원 스탭",
    OTHER: "기타",
    CORPORATE_MANAGER: "기업 담당자",
  };
  return labels[role] || role || "";
};

const hospitalDisplayName = (record) => {
  if (record?.role === "CORPORATE_MANAGER" && record?.companyName) {
    return record.companyName;
  }
  return record?.hospitalName || "-";
};

const formatDate = (value) => {
  if (!value) return "-";
  const date = dayjs(value);
  return date.isValid() ? date.format("YYYY-MM-DD HH:mm") : "-";
};

onMounted(() => {
  fetchPromotion();
  fetchEntries();
});
</script>

<style scoped>
.entries-page {
  min-height: 100%;
  padding: 28px 24px 48px;
  background: #f6f8fb;
  color: #1f2937;
}

.entries-header {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 20px;
  margin-bottom: 18px;
}

.back-button {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  margin: 0 0 12px;
  padding: 0;
  border: 0;
  background: transparent;
  color: #425466;
  font-size: 13px;
  font-weight: 700;
}

.back-button:hover {
  color: #1677ff;
}

.eyebrow {
  margin: 0 0 6px;
  color: #3f6b57;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0;
  text-transform: uppercase;
}

.entries-header h1 {
  margin: 0;
  font-size: 28px;
  font-weight: 800;
  letter-spacing: 0;
}

.description {
  margin: 8px 0 0;
  color: #657184;
  font-size: 14px;
}

.header-actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 8px;
}

.header-actions :deep(.ant-btn) {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  min-height: 38px;
}

.filter-panel,
.table-panel {
  border: 1px solid #dbe3ed;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 10px 30px rgba(31, 41, 55, 0.04);
}

.filter-panel {
  display: grid;
  grid-template-columns: minmax(360px, 1fr) 180px max-content;
  align-items: center;
  gap: 12px;
  padding: 18px;
  margin-bottom: 14px;
}

.search-combo {
  display: grid;
  grid-template-columns: 132px minmax(220px, 1fr);
  min-width: 0;
}

.search-field :deep(.ant-select-selector) {
  border-radius: 6px 0 0 6px;
}

.search-combo :deep(.ant-input-group .ant-input) {
  border-left: 0;
}

.status-filter {
  width: 180px;
}

.selected-chip {
  min-width: 92px;
  padding: 8px 12px;
  border: 1px solid #dbe3ed;
  border-radius: 999px;
  color: #6f7f90;
  background: #f7f9fb;
  font-size: 13px;
  font-weight: 700;
  text-align: center;
}

.selected-chip.active {
  border-color: #9fd2b4;
  color: #214f3b;
  background: #edf8f1;
}

.table-panel {
  overflow: hidden;
}

.table-summary {
  display: flex;
  align-items: baseline;
  gap: 6px;
  padding: 15px 18px;
  border-bottom: 1px solid #edf1f5;
}

.table-summary strong {
  color: #214f3b;
  font-size: 22px;
}

.table-summary span {
  color: #667386;
  font-size: 13px;
}

.table-panel :deep(.ant-table-wrapper) {
  overflow-x: auto;
}

.table-panel :deep(.ant-table-tbody > tr) {
  cursor: pointer;
}

.table-panel :deep(.ant-table-tbody > tr > td) {
  vertical-align: top;
}

.person-cell {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.person-cell strong,
.person-cell span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.person-cell span {
  margin-top: 3px;
  color: #8a95a5;
  font-size: 12px;
}

.mono {
  font-variant-numeric: tabular-nums;
}

.consent-list {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.consent-pill {
  display: inline-flex;
  padding: 4px 8px;
  border: 1px solid #e5eaf0;
  border-radius: 999px;
  background: #f7f9fb;
  color: #6f7f90;
  font-size: 12px;
}

.consent-pill.agreed {
  border-color: #9fd2b4;
  background: #edf8f1;
  color: #214f3b;
}

@media (max-width: 1200px) {
  .entries-header {
    align-items: flex-start;
    flex-direction: column;
  }

  .header-actions {
    justify-content: flex-start;
  }

  .filter-panel {
    grid-template-columns: 1fr 180px;
  }

  .selected-chip {
    justify-self: start;
  }
}

@media (max-width: 760px) {
  .entries-page {
    padding: 20px 14px 36px;
  }

  .filter-panel {
    grid-template-columns: 1fr;
  }

  .search-combo {
    grid-template-columns: 1fr;
  }

  .search-field :deep(.ant-select-selector) {
    border-radius: 6px;
  }

  .search-combo :deep(.ant-input-group .ant-input) {
    border-left: 1px solid #d9d9d9;
  }

  .status-filter {
    width: 100%;
  }
}
</style>
